import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token management
const getAccessToken = () => localStorage.getItem('admin_access_token');
const getRefreshToken = () => localStorage.getItem('admin_refresh_token');
const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem('admin_access_token', accessToken);
  if (refreshToken) {
    localStorage.setItem('admin_refresh_token', refreshToken);
  }
};
const clearTokens = () => {
  localStorage.removeItem('admin_access_token');
  localStorage.removeItem('admin_refresh_token');
  localStorage.removeItem('admin_user');
};

// Request interceptor - add auth token to requests
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add auth token to all requests except auth endpoints
    if (!config.url?.includes('/auth/')) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh and auth errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    console.error('API Response Error:', error.response?.data || error.message);
    
    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry && error.response?.data?.code === 'TOKEN_EXPIRED') {
      originalRequest._retry = true;
      
      try {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          console.log('Token expired, attempting refresh...');
          const refreshResponse = await axios.post(`${API_BASE_URL}/auth/admin/refresh`, {
            refreshToken
          });
          
          const newAccessToken = refreshResponse.data.accessToken;
          setTokens(newAccessToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Refresh failed, redirect to login
        clearTokens();
        window.location.reload(); // This will show login screen
        return Promise.reject(refreshError);
      }
    }
    
    // Handle other auth errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Invalid token or insufficient permissions
      console.warn('Authentication failed, clearing tokens');
      clearTokens();
      window.location.reload(); // This will show login screen
    }
    
    return Promise.reject(error);
  }
);

export const adminApi = {
  // Authentication
  login: (credentials) => api.post('/auth/admin/login', credentials),
  loginWithGoogle: (googleToken) => api.post('/auth/admin/login', { google_token: googleToken }),
  logout: () => {
    const token = getAccessToken();
    const logoutPromise = token ? api.post('/auth/admin/logout') : Promise.resolve();
    clearTokens();
    return logoutPromise;
  },
  refreshToken: (refreshToken) => api.post('/auth/admin/refresh', { refreshToken }),
  verifyPin: (adminId, pin) => api.post('/auth/admin/verify-pin', { adminId, pin }),
  
  // Auth utilities
  isAuthenticated: () => !!getAccessToken(),
  getCurrentUser: () => {
    const user = localStorage.getItem('admin_user');
    return user ? JSON.parse(user) : null;
  },
  clearAuth: clearTokens,
  
  // Get all volunteers
  getVolunteers: () => api.get('/admin/volunteers'),

  // Approve volunteer
  approveVolunteer: (email, data) => api.post(`/admin/volunteers/${email}/approve`, data),

  // Update volunteer status
  updateVolunteerStatus: (email, data) => api.patch(`/admin/volunteers/${email}/status`, data),
  
  
  // Entity management
  getEntities: () => api.get('/entidades'),
  createEntity: (data) => api.post('/entidades', data),
  updateEntity: (id, data) => api.put(`/entidades/${id}`, data),
  deleteEntity: (id) => api.delete(`/entidades/${id}`),
  
  // Events management
  getEvents: (params) => api.get('/eventos', { params }),
  getEventsStats: () => api.get('/eventos/stats'),
  getEventById: (id) => api.get(`/eventos/${id}`),
  updateEvent: (id, data) => api.put(`/eventos/${id}`, data),
  deleteEvent: (id) => api.delete(`/eventos/${id}`),
  
  // Rondas (Routes) management
  getRondas: () => api.get('/rondas'),
  createRonda: (data) => api.post('/rondas', data),
  updateRonda: (id, data) => api.put(`/rondas/${id}`, data),

  // Visited locations (locais_visitados) management
  getLocais: () => api.get('/locais/admin'),
  createLocal: (data) => api.post('/locais/admin', data),
  updateLocal: (id, data) => api.put(`/locais/admin/${id}`, data),
  deleteLocal: (id) => api.delete(`/locais/admin/${id}`),
  
  // Get system statistics
  getStats: () => api.get('/admin/stats'),
  
  // Admin management (master admin only)
  getAdmins: () => api.get('/admin/admins'),
  createAdmin: (data) => api.post('/admin/admins', data),
  updateAdmin: (id, data) => api.put(`/admin/admins/${id}`, data),
  deleteAdmin: (id) => api.delete(`/admin/admins/${id}`),
  

};

export default api;